import { Suspense, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useWindowScroll, useWindowSize } from 'react-use';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { useTranslation } from '@hooks';
import { useFilter } from '@context/FilterContext';
import {
    FilterButton,
    SortButton,
    ListingViewSection,
} from '@components/listing';
import convertCategoryName from '@utils/convertCategoryName';
import { DefectiveSaleWarning } from '@components/ui';

const SearchNotFound = dynamic(() =>
    import('components/listing/SearchNotFound'),
);
const ScrollTopButton = dynamic(() =>
    import('components/listing/ScrollTopButton'),
);
const FilterModal = dynamic(() => import('components/listing/FilterModal'));

const ListingMobileHeader = dynamic(() =>
    import('components/listing/ListingMobileHeader'),
);

const SortOptionModal = dynamic(() =>
    import('components/listing/SortOptionModal'),
);

const ListingCategoryDescriptions = dynamic(
    () => import('components/listing/ListingCategoryDescription'),
    {
        suspense: true,
    },
);
const PaginationState = dynamic(() =>
    import('components/listing/PaginationState'),
);
const ListingLayout = ({
    category,
    categoryDescription,
    hasNextProducts,
    fetchNextPage,
    loadMore,
    filters,
    isLoading,
    listStyle,
    productCount,
    children,
    listingType,
    changeListingType,
}) => {
    const router = useRouter();
    const { width } = useWindowSize();
    const { y } = useWindowScroll();
    const { t, locale } = useTranslation();
    const [sortModalIsOpen, setSortModalIsOpen] = useState(false);
    const [filterModalIsOpen, setFilterModalIsOpen] = useState(false);

    const {
        selectOptionsfromQuery,
        onOrderChange,
        selectedOrder,
        selectedFilterCount,
        onKeywordChange,
    } = useFilter();

    const openFilterModal = () => setFilterModalIsOpen(true);
    const closeFilterModal = () => setFilterModalIsOpen(false);

    const openSortModal = () => setSortModalIsOpen(true);
    const closeSortModal = () => setSortModalIsOpen(false);

    useEffect(() => {
        if (router.query.keyword) {
            onKeywordChange(router.query.keyword);
        }
    }, [router.query.keyword]);

    useEffect(() => {
        if (router.query.keyword) {
            onKeywordChange(router.query.keyword);
        }
        if (router.isReady) {
            selectOptionsfromQuery(router.query);
        }
    }, [router.isReady]);

    const changeSort = (value) => {
        onOrderChange(value);
        setSortModalIsOpen(false);
    };
    const isDefectiveCategory = router.query.category.match(
        /^(defolu|defective)$/,
    );
    return (
        <>
            <Wrapper
                isScrolling={y > 30}
                isDefectiveCategory={isDefectiveCategory}
            >
                {isDefectiveCategory && (
                    <DefectiveSaleWarning
                        listing
                        text={t('defectiveWarningListing')}
                    />
                )}
                <FilterAndListingWrapper
                    isScrolling={y > 30}
                    hidden={width < 1024}
                >
                    <ListingViewSection
                        isDesktop
                        listingType={listingType}
                        changeListingType={changeListingType}
                    />
                    <FilterButtons y={y}>
                        <SortButton
                            title={t('sortBy')}
                            onSortModalOpen={openSortModal}
                            data-testid="sort-button"
                        />
                        <FilterButton
                            title={t('filter')}
                            filterCount={selectedFilterCount.all}
                            productCount={productCount}
                            onFilterModalOpen={openFilterModal}
                        />
                    </FilterButtons>
                </FilterAndListingWrapper>
                <ListingMobileHeader
                    category={category}
                    filterCount={selectedFilterCount.all}
                    productCount={productCount}
                    openFilterModal={openFilterModal}
                    openSortModal={openSortModal}
                    listingType={listingType}
                    changeListingType={changeListingType}
                />
                <FilterModal
                    filters={filters}
                    filterModalIsOpen={filterModalIsOpen}
                    productCount={productCount}
                    onCloseFilterModal={closeFilterModal}
                />
                <SortOptionModal
                    title={t('sort')}
                    selectedOrder={selectedOrder}
                    isOpen={sortModalIsOpen}
                    onCloseSortModal={closeSortModal}
                    onOrderChange={changeSort}
                />

                <ListLayout listStyle={listStyle} listingType={listingType}>
                    {children}
                </ListLayout>
                {productCount < 1 && (
                    <SearchNotFound withOutButton filterNotFound />
                )}
                {y > 1200 && <ScrollTopButton />}
            </Wrapper>
            <>
                <PaginationState
                    hasNextProducts={hasNextProducts}
                    fetchNextPage={fetchNextPage}
                    loadRef={loadMore}
                    loading={isLoading}
                    totalProducts={productCount}
                />
                {/* <CategoriesMayBeInterested /> */}
                {locale === 'tr' && (
                    <Suspense fallback={<p>loading...</p>}>
                        <ListingCategoryDescriptions
                            data={categoryDescription}
                            title={convertCategoryName(
                                category.replace(/-/g, ' '),
                            )}
                            productCount={productCount}
                        />
                    </Suspense>
                )}
            </>
        </>
    );
};
const Wrapper = styled.div`
    margin-top: 120px;
    position: relative;
    min-height: calc(100vh - 120px);
    @media (max-width: 1024px) {
        margin-top: 207px;
    }
    @media (max-width: 768px) {
        margin-top: ${({ isScrolling }) => (isScrolling ? '145px' : '162px')};
        margin-top: ${({ isDefectiveCategory }) =>
            isDefectiveCategory && '308px'};
        min-height: calc(100vh - 160px);
    }
    z-index: 8;
`;
const ListLayout = styled.section`
    display: grid;
    justify-content: center;
    justify-items: center;
    grid-template-columns: ${({ listingType }) => listingType.column};

    @media (max-width: 768px) {
        grid-template-columns: ${({ listingType }) => listingType.column};
    }
`;

const FilterAndListingWrapper = styled.div`
    display: flex;
    flex-direction: row-reverse;
    gap: 50px;
    align-items: center;
    z-index: 100;
    background: white;
    transition: all 0.3s ease-in-out;
    width: 100%;
    border: 1px solid black;
    border-bottom: none;
    position: ${({ isScrolling }) => (isScrolling ? 'fixed' : 'relative')};
    top: ${({ isScrolling }) => isScrolling && '88px'};
    border-bottom: ${({ isScrolling }) => isScrolling && '1px solid black'};

    @media (max-width: 1024px) {
        flex-direction: row;
        display: none;
    }
`;

const FilterButtons = styled.div`
    display: flex;
    gap: 40px;
    align-items: center;
    justify-content: flex-start;
    padding-left: 15px;
    width: 100%;

    @media (max-width: 1024px) {
        display: none;
    }
`;
export default ListingLayout;
